import { useEffect, useRef } from 'react';
import { useAuth } from 'auth';
import { useSaveUserConfigSetting } from './mutate';
import { ACTIVITY_CUSTOM_CHARTS_KEY, useCustomChartsInCloud } from 'reporting/edit-mode/custom-charts';

// migrator for local storage items
// this fires on login, any items in migratedSettings will move from their storageKey locally to settingKey in the cloud
// the storageKey is deleted afterwards

export const migratedSettings = (userId: string): { storageKey: string; settingKey: string }[] => [
    { storageKey: `${userId}-ar-athletes-saved-items`, settingKey: ACTIVITY_CUSTOM_CHARTS_KEY },
];

const readLocal = (storageKey: string): unknown => {
    const raw = localStorage.getItem(storageKey);

    if (!raw) {
        return undefined;
    }

    try {
        return JSON.parse(raw);
    } catch {
        return undefined; // this cant be a real value so dont bother uploading it to the service
    }
};

export const useMigrateLocalSettings = () => {
    const { isAuthenticated, user } = useAuth();
    const { mutateAsync: saveSetting } = useSaveUserConfigSetting();
    const chartsInCloud = useCustomChartsInCloud();
    const migratedRef = useRef(false);

    useEffect(() => {
        if (!chartsInCloud || !isAuthenticated || !user?.id || migratedRef.current) {
            return;
        }

        migratedRef.current = true;

        void (async () => {
            for (const { storageKey, settingKey } of migratedSettings(user.id)) {
                const value = readLocal(storageKey);

                if (value === undefined) {
                    continue;
                }

                try {
                    await saveSetting({ settingKey, value });
                    localStorage.removeItem(storageKey);
                } catch {
                    // failures are left in place until the next attempt
                }
            }
        })();
    }, [chartsInCloud, isAuthenticated, user?.id, saveSetting]);
};
